import { computed, defineComponent, isRef, type FunctionalComponent, type VNode } from 'vue'
import type { IDataTableColumnProps } from './types'
import { isFunctionalComponent } from '@/utils/is'
import { tv } from 'tailwind-variants'
import { BODY_ROW_HEIGHT_PX, HEAD_ROW_HEIGHT_PX } from '../../constants'
import SortIcon from 'assets/img/svg/icons/sort3.svg?url'
import Icon from 'components/icon'
import { twColor } from '@/utils/style'
import { uref } from '@/utils/reactive/uref'
import { useResponsive } from '~/composables/useResponsive'

const clazz = tv({
  base: 'px-4 border-gray-200',
  variants: {
    head: {
      true: `text-gray-500 text-xs font-semibold uppercase border-t border-b border-gray-300`,
      false: `text-gray-500 text-xs font-medium border-b`
    },
    borderless: {
      true: 'border-none'
    },
    borderlessTop: {
      true: 'border-t-0 border-top-none'
    }
  }
})

export const DataColumn = defineComponent<IDataTableColumnProps & { borderlessTop?: boolean; }>(
  (props) => {
    const height = computed(() => (props.head ? HEAD_ROW_HEIGHT_PX : BODY_ROW_HEIGHT_PX));
    const responsive = useResponsive();

    const content = computed(() => {
      const body = uref(props.column).body;

      if (isRef<VNode>(body)) {
        return uref(body);
      }

      const InnerContent = body ? uref(body) : undefined;

      if (typeof InnerContent === 'undefined') return ''
      return isFunctionalComponent(InnerContent) ? <InnerContent /> : InnerContent + ''
    });

    const dataCol = computed(() => unref(props.column));

    const Wrapper: FunctionalComponent<any> = props.head
      ? (props, { slots }) => <th {...props}>{slots.default ? slots.default() : ''}</th>
      : (props, { slots }) => <td {...props}>{slots.default ? slots.default() : ''}</td>

    const handleClick = () => {
      if (props.disableSorting) return;
      const col = uref(props.column);
      if (col.sortable === false) return;
      const key = col.sortKey || col.key || col.label;
      if (!key) return;
      props.table.state.toggleSortDirection(key);
      if (props.table.options.sort?.onChange) {
        props.table.options.sort.onChange();
      }
      //const state = uref(props.column).state
      //if (!state) return

      //const nextState = state.value;

      //if (state.value.sort <= -1) {
      //  nextState.sort = 0
      //} else if (Math.abs(nextState.sort) === 0) {
      //  nextState.sort = 1
      //} else if (nextState.sort >= 1) {
      //  nextState.sort = -1
      //}


      //state.value = nextState
    }

    const sortDirection = computed(() => {
      const col = uref(props.column);
      if (col.sortable === false) return 0;
      const key = col.sortKey || col.key || col.label;
      if (!key) return 0;
      const slot = props.table.state.getSortSlot(key);
      if (!slot) return 0;
      return slot.value.direction;
      //const state = uref(props.column).state
      //if (!state) return 0
      //return state.value.sort
    })

    const pathOptions = computed(() => {
      if (sortDirection.value === 0) {
        return [
          {
            stroke: twColor('gray-500')
          },
          {
            stroke: twColor('gray-500')
          }
        ]
      } else if (sortDirection.value === -1) {
        return [
          {
            stroke: twColor('primary-500')
          },
          {
            stroke: twColor('gray-500')
          }
        ]
      }

      return [
        {
          stroke: twColor('gray-500')
        },
        {
          stroke: twColor('primary-500')
        }
      ]
    })

    return () => {
      return (
        <Wrapper
          class={clazz({ head: !!props.head, borderless: !!props.borderless, borderlessTop: props.borderlessTop })}
          style={{
            position: 'relative',
            minHeight: height.value + 'px',
            userSelect: 'none',
            height: height.value + 'px',
            ...(uref(props.column).align ? {
              textAlign: uref(props.column).align
            } : {}),
            ...(uref(props.column).direction ? {
              direction: uref(props.column).direction
            } : {}),
            ...(responsive.isSmall.value ? {
              minWidth: unref(props.column).mobileWidth || '128px'
            } : {
              ...(uref(props.column).width ? {
                width: `${uref(props.column).width}%`
              } : {}),
            })
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            height: height.value + 'px',
            position: 'relative'
          }}>
            <div
              {...(uref(props.column).state
                ? {
                  onClick: handleClick
                }
                : {})}
              style={{
                maxWidth: 'fit-content',
                ...(uref(props.column).state
                  ? {
                    display: 'grid',
                    gridTemplateColumns: 'max-content max-content',
                    gap: '0.5rem',
                    cursor: 'pointer',
                    alignItems: 'center'
                  }
                  : {})
              }}
            >
              <div style={{
                maxWidth: 'fit-content',
                maxHeight: height.value + 'px'
              }}>
                {content.value}
              </div>
              {uref(props.column).state && !props.disableSorting && dataCol.value.sortable !== false ? (
                <Icon
                  src={SortIcon}
                  key={`sort-${sortDirection.value}`}
                  pathOptions={pathOptions.value}
                  fill="currentColor"
                  size={'1rem'}
                />
              ) : null}
            </div>
          </div>
        </Wrapper>
      )
    }
  },
  { props: ['column', 'head', 'borderless', 'borderlessTop', 'table', 'disableSorting'] }
)
